import * as THREE from 'three';
import vertexShader from './shaders/instance.vert?raw';
import fragmentShader from './shaders/instance.frag?raw';
import { buildTextAtlas, uvForChar } from './text-atlas.js';

// Builds an InstancedMesh of `count` quads. Caller drives per-instance
// glyph (UV rect), color, transform via setters or by writing the
// underlying attribute arrays directly + bumping needsUpdate.
export async function buildInstancer({ count, glyphChars, glyphMap, atlas, material, quadSize = 1.0 }) {
  const { texture, uvMap } = await buildTextAtlas(glyphChars, atlas, glyphMap);

  const geometry = new THREE.PlaneGeometry(quadSize, quadSize);

  const aUVRect = new Float32Array(count * 4);
  const aOffset = new Float32Array(count * 3);
  const aRotation = new Float32Array(count * 3);
  const aScale = new Float32Array(count * 3);
  const aColor = new Float32Array(count * 3);

  // initialise to identity scale + space glyph + white
  const blank = uvForChar(uvMap, ' ', glyphChars[0]);
  for (let i = 0; i < count; i++) {
    aUVRect[i * 4 + 0] = blank.u0;
    aUVRect[i * 4 + 1] = blank.v0;
    aUVRect[i * 4 + 2] = blank.u1;
    aUVRect[i * 4 + 3] = blank.v1;
    aScale[i * 3 + 0] = 1;
    aScale[i * 3 + 1] = 1;
    aScale[i * 3 + 2] = 1;
    aColor[i * 3 + 0] = 1;
    aColor[i * 3 + 1] = 1;
    aColor[i * 3 + 2] = 1;
  }

  geometry.setAttribute('aUVRect', new THREE.InstancedBufferAttribute(aUVRect, 4));
  geometry.setAttribute('aOffset', new THREE.InstancedBufferAttribute(aOffset, 3));
  geometry.setAttribute('aRotation', new THREE.InstancedBufferAttribute(aRotation, 3));
  geometry.setAttribute('aScale', new THREE.InstancedBufferAttribute(aScale, 3));
  geometry.setAttribute('aColor', new THREE.InstancedBufferAttribute(aColor, 3));

  const shaderMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uAtlas: { value: texture },
      uTint: { value: new THREE.Color(material?.tint ?? 0xffffff) },
      uEmissive: { value: material?.emissiveBoost ?? 1.6 },
      uMonochrome: { value: material?.monochrome ?? 0 },
    },
    transparent: true,
    blending: material?.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    depthWrite: false,
    depthTest: false,
  });

  const mesh = new THREE.InstancedMesh(geometry, shaderMaterial, count);
  mesh.frustumCulled = false;

  return {
    mesh,
    material: shaderMaterial,
    atlasTexture: texture,
    uvMap,
    attributes: {
      aUVRect: geometry.getAttribute('aUVRect'),
      aOffset: geometry.getAttribute('aOffset'),
      aRotation: geometry.getAttribute('aRotation'),
      aScale: geometry.getAttribute('aScale'),
      aColor: geometry.getAttribute('aColor'),
    },
    setGlyph(i, ch) {
      const r = uvForChar(uvMap, ch);
      const a = aUVRect;
      a[i * 4 + 0] = r.u0; a[i * 4 + 1] = r.v0;
      a[i * 4 + 2] = r.u1; a[i * 4 + 3] = r.v1;
    },
    setColor(i, r, g, b) {
      aColor[i * 3 + 0] = r;
      aColor[i * 3 + 1] = g;
      aColor[i * 3 + 2] = b;
    },
    setOffset(i, x, y, z = 0) {
      aOffset[i * 3 + 0] = x;
      aOffset[i * 3 + 1] = y;
      aOffset[i * 3 + 2] = z;
    },
    setScale(i, sx, sy = sx, sz = sx) {
      aScale[i * 3 + 0] = sx;
      aScale[i * 3 + 1] = sy;
      aScale[i * 3 + 2] = sz;
    },
    flushAll() {
      this.attributes.aUVRect.needsUpdate = true;
      this.attributes.aOffset.needsUpdate = true;
      this.attributes.aRotation.needsUpdate = true;
      this.attributes.aScale.needsUpdate = true;
      this.attributes.aColor.needsUpdate = true;
    },
  };
}
